import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Loader2, Zap, Car, CreditCard, FileText, BarChart3, Star,
  ShieldCheck, ShieldAlert, Search, MapPin, TrendingUp, Wallet,
  Users, CheckCircle2, XCircle, Clock, Siren,
} from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

function fmtXAF(n) {
  return `${Number(n || 0).toLocaleString("fr-FR").replace(/,/g, " ")} F`;
}

function StatusPill({ status }) {
  const map = {
    completed:   { text: "Terminée",  cls: "bg-emerald-50 text-emerald-700" },
    in_progress: { text: "En cours",  cls: "bg-amber-50 text-amber-700" },
    confirmed:   { text: "Confirmée", cls: "bg-blue-50 text-blue-700" },
    pending:     { text: "En attente",cls: "bg-slate-100 text-slate-600" },
    cancelled:   { text: "Annulée",   cls: "bg-rose-50 text-rose-700" },
  };
  const v = map[status] || { text: status || "—", cls: "bg-slate-100 text-slate-600" };
  return <span className={`inline-block text-[10.5px] font-bold px-2 py-0.5 rounded ${v.cls}`}>{v.text}</span>;
}

/* ---------------- Express Bookings ---------------- */
export function ExpressBookingsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/express/bookings")
      .then(({ data }) => setItems(data || []))
      .catch(() => toast.error("Impossible de charger les courses Express"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((b) => {
    if (status !== "all" && b.status !== status) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [b.user_name, b.driver_name, b.origin, b.destination, b.id, b.otp_code]
      .some((v) => (v ?? "").toString().toLowerCase().includes(s));
  });

  return (
    <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4" data-testid="admin-express-bookings-panel">
      <div className="flex items-center gap-2 mb-4">
        <Car className="w-5 h-5 text-[#F2A93B]" />
        <h2 className="text-lg font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Courses Express</h2>
        <span className="text-xs text-[#5B6B7A] ml-auto">
          <b className="text-[#0F2B46]">{items.length}</b> courses au total
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-[#94A3B8]" />
          <Input placeholder="Rechercher (client, chauffeur, OTP, origine…)"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9" data-testid="express-bookings-search" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {[
            { id: "all", label: "Tous" },
            { id: "pending", label: "En attente" },
            { id: "confirmed", label: "Confirmées" },
            { id: "in_progress", label: "En cours" },
            { id: "completed", label: "Terminées" },
            { id: "cancelled", label: "Annulées" },
          ].map((s) => (
            <Button key={s.id} type="button" onClick={() => setStatus(s.id)}
              variant={status === s.id ? "default" : "outline"} size="sm"
              className={status === s.id ? "bg-[#0F2B46] text-white" : ""}
              data-testid={`express-bookings-filter-${s.id}`}>
              {s.label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#0F2B46]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#5B6B7A]">Aucune course Express ne correspond aux filtres.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">
                <th className="py-1.5 pr-2">Client</th>
                <th className="py-1.5 pr-2">Chauffeur</th>
                <th className="py-1.5 pr-2">Trajet</th>
                <th className="py-1.5 pr-2">Statut</th>
                <th className="py-1.5 pr-2">OTP</th>
                <th className="py-1.5 pr-2 text-right">Montant</th>
                <th className="py-1.5 pr-2">Créée le</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-b border-[#F0EDE6]" data-testid={`express-booking-row-${b.id}`}>
                  <td className="py-2 pr-2 font-semibold text-[#142433]">{b.user_name || "—"}<div className="text-[11px] text-[#5B6B7A]">{b.user_phone || ""}</div></td>
                  <td className="py-2 pr-2">{b.driver_name || <span className="text-[#94A3B8] italic">Non attribué</span>}</td>
                  <td className="py-2 pr-2 max-w-[240px] truncate" title={`${b.origin || ""} → ${b.destination || ""}`}>
                    <MapPin className="w-3 h-3 inline text-[#5B6B7A] mr-1" />
                    {b.origin || "?"} → {b.destination || "?"}
                  </td>
                  <td className="py-2 pr-2"><StatusPill status={b.status} /></td>
                  <td className="py-2 pr-2 font-mono">{b.otp_code || "—"}</td>
                  <td className="py-2 pr-2 text-right font-bold text-[#0F2B46]">{fmtXAF(b.total_amount)}</td>
                  <td className="py-2 pr-2 text-[#5B6B7A]">{(b.created_at || "").split("T")[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Express Payments ---------------- */
export function ExpressPaymentsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/express/payments")
      .then(({ data }) => setItems(data || []))
      .catch(() => toast.error("Impossible de charger les paiements Express"))
      .finally(() => setLoading(false));
  }, []);

  const total = items.reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4" data-testid="admin-express-payments-panel">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard className="w-5 h-5 text-[#F2A93B]" />
        <h2 className="text-lg font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Paiements Express</h2>
        <span className="text-xs text-[#5B6B7A] ml-auto">
          Total : <b className="text-[#0F2B46]">{fmtXAF(total)}</b> · <b className="text-[#0F2B46]">{items.length}</b> paiements
        </span>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#0F2B46]" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#5B6B7A]">Aucun paiement lié aux courses Express.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">
                <th className="py-1.5 pr-2">Réf</th>
                <th className="py-1.5 pr-2">Méthode</th>
                <th className="py-1.5 pr-2">Statut</th>
                <th className="py-1.5 pr-2 text-right">Montant</th>
                <th className="py-1.5 pr-2">Course</th>
                <th className="py-1.5 pr-2">Créé le</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-[#F0EDE6]" data-testid={`express-payment-row-${p.id}`}>
                  <td className="py-2 pr-2 font-mono text-[11px]">{p.transaction_ref || p.id.slice(-8)}</td>
                  <td className="py-2 pr-2">{p.method || "—"}</td>
                  <td className="py-2 pr-2">
                    <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded ${
                      p.status === "success" || p.status === "paid" ? "bg-emerald-50 text-emerald-700" :
                      p.status === "pending" ? "bg-amber-50 text-amber-700" :
                      "bg-rose-50 text-rose-700"
                    }`}>{p.status || "—"}</span>
                  </td>
                  <td className="py-2 pr-2 text-right font-bold text-[#0F2B46]">{fmtXAF(p.amount)}</td>
                  <td className="py-2 pr-2 font-mono text-[11px]">{(p.booking_id || "").slice(-8)}</td>
                  <td className="py-2 pr-2 text-[#5B6B7A]">{(p.created_at || "").split("T")[0]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Express Documents / KYC ---------------- */
export function ExpressDocumentsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/express/documents")
      .then(({ data }) => setItems(data.drivers || []))
      .catch(() => toast.error("Impossible de charger les documents Express"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4" data-testid="admin-express-documents-panel">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-[#F2A93B]" />
        <h2 className="text-lg font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Documents / KYC — Chauffeurs Express</h2>
        <span className="text-xs text-[#5B6B7A] ml-auto"><b className="text-[#0F2B46]">{items.length}</b> chauffeurs intégrés</span>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#0F2B46]" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#5B6B7A]">Aucun chauffeur intégré à Trigo Express pour le moment.</div>
      ) : (
        <div className="space-y-2">
          {items.map((d) => {
            const pct = d.documents_required ? Math.round((d.documents_completed / d.documents_required) * 100) : 0;
            const ok = pct === 100 && d.driver_status === "approved";
            return (
              <div key={d.id} className="p-3 rounded-xl border border-[#E4E0D8] flex flex-wrap items-center gap-3" data-testid={`express-doc-row-${d.id}`}>
                <div className="w-9 h-9 rounded-lg bg-[#F4F2ED] text-[#0F2B46] font-extrabold text-xs flex items-center justify-center shrink-0">
                  {(d.name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold text-[#142433] text-sm">{d.name}</div>
                  <div className="text-[11px] text-[#5B6B7A]">{d.phone || d.email}</div>
                </div>
                <div className="min-w-[130px]">
                  <div className="text-[10.5px] text-[#5B6B7A] mb-0.5">Documents: {d.documents_completed}/{d.documents_required}</div>
                  <div className="w-full bg-[#F0EDE6] rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div>
                  {ok ? (
                    <span className="text-[10.5px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Complet
                    </span>
                  ) : (
                    <span className="text-[10.5px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> À compléter
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- Express Stats ---------------- */
function StatKPI({ Icon, label, value, hint }) {
  return (
    <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4">
      <div className="flex items-center gap-1.5 text-[11.5px] text-[#5B6B7A] mb-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="text-xl font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>{value}</div>
      {hint && <div className="text-[11px] text-[#5B6B7A] mt-1">{hint}</div>}
    </div>
  );
}

export function ExpressStatsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/express/stats")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Impossible de charger les statistiques Express"))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="bg-white border border-[#E4E0D8] rounded-2xl p-8 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F2B46]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-express-stats-panel" className="space-y-4">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-[#F2A93B]" />
        <h2 className="text-lg font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Statistiques Trigo Express</h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatKPI Icon={Car}       label="Courses totales"      value={data.total_bookings}   hint={`${data.bookings_today} aujourd'hui`} />
        <StatKPI Icon={CheckCircle2} label="Terminées"         value={data.completed}        hint={`${data.completed_today} aujourd'hui`} />
        <StatKPI Icon={Clock}     label="En cours"             value={data.in_progress} />
        <StatKPI Icon={XCircle}   label="Annulées"             value={data.cancelled} />
        <StatKPI Icon={Wallet}    label="Revenu total"         value={fmtXAF(data.revenue_xaf)} />
        <StatKPI Icon={Star}      label="Note moyenne"         value={data.avg_rating || "—"} />
        <StatKPI Icon={Users}     label="Chauffeurs intégrés"  value={data.total_drivers} />
        <StatKPI Icon={TrendingUp} label="Chauffeurs en ligne" value={data.online_drivers} />
      </div>
    </div>
  );
}

/* ---------------- Express Alerts ---------------- */
export function ExpressAlertsPanel({ liveData }) {
  const alerts = (liveData && liveData.alerts) || [];
  return (
    <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4" data-testid="admin-express-alerts-panel">
      <div className="flex items-center gap-2 mb-4">
        <Siren className="w-5 h-5 text-[#C1453B]" />
        <h2 className="text-lg font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Alertes Trigo Express</h2>
        <span className="text-xs text-[#5B6B7A] ml-auto"><b className="text-[#0F2B46]">{alerts.length}</b> alerte(s)</span>
      </div>
      {alerts.length === 0 ? (
        <div className="py-8 text-center text-sm text-[#5B6B7A]">Aucune alerte active sur les opérations Express 🎉</div>
      ) : (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className="p-3 rounded-xl border border-[#E4E0D8] flex items-start gap-3" data-testid={`express-alert-item-${i}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                a.type === "sos" ? "bg-red-50 text-red-600" :
                a.type === "doc" ? "bg-amber-50 text-amber-600" :
                "bg-blue-50 text-blue-600"
              }`}>
                {a.type === "sos" ? <ShieldAlert className="w-4 h-4" /> :
                 a.type === "doc" ? <FileText className="w-4 h-4" /> :
                 <Star className="w-4 h-4" />}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#142433]">{a.title}</div>
                <div className="text-[11.5px] text-[#5B6B7A]">{a.meta}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
